import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import moment from 'moment-timezone'
import { useErrorService, ABSpinner } from '@abroad/components'
import { ExperienceSelect } from '../form'
import { formatDate, UTCformatDateInPlan } from '../../utils/date'
import API from '../../utils/API'

const CopyItineraryModal = ({
  show,
  onHide,
  selectedQuest,
  sourceQuestId,
  currentQuestStartDate,
  currentQuestEndDate,
  onConfirmCopy,
  isLoading = false,
  title = 'Copy itinerary',
  confirmLabel = 'Copy itinerary',
  showOriginalLink = true,
  lead = 'Bring days from another experience onto this one. Programme dates stay as they are. Day 1 copies onto Day 1, and so on.',
}) => {
  const [oldItineraryDates, setOldItineraryDates] = useState([])
  const [oldItineraryData, setOldItineraryData] = useState([])
  const [newItineraryDates, setNewItineraryDates] = useState([])
  const [itineraryMapping, setItineraryMapping] = useState([])
  const [isLoadingItinerary, setIsLoadingItinerary] = useState(false)
  const Error = useErrorService()
  const selectedQuestId = selectedQuest?.value

  const generateNewDateSequence = useCallback(
    (startDate, endDate, oldDates) => {
      if (!startDate || !endDate) {
        setNewItineraryDates([])
        setItineraryMapping([])
        return
      }

      const start = moment.utc(startDate)
      const end = moment.utc(endDate)

      if (!start.isValid() || !end.isValid() || start.isAfter(end)) {
        setNewItineraryDates([])
        setItineraryMapping([])
        return
      }

      const newDates = []
      const current = start.clone()

      while (current.isSameOrBefore(end, 'day')) {
        newDates.push(formatDate(current))
        current.add(1, 'day')
      }

      setNewItineraryDates(newDates)

      const oldCount = oldDates.length
      const mappingRows = newDates.map((newDate, index) => ({
        id: index,
        oldDate: index < oldCount ? oldDates[index] : 'Keep Empty',
        newDate: newDate,
      }))

      setItineraryMapping(mappingRows)
    },
    [],
  )

  useEffect(() => {
    const fetchItineraryDates = async () => {
      const itineraryQuestId = sourceQuestId || selectedQuestId
      if (!itineraryQuestId || !show) {
        return
      }

      try {
        setIsLoadingItinerary(true)
        const { data } = await API.AdminAPI.quests.getItinerary(
          itineraryQuestId,
        )

        if (data?.days && Array.isArray(data.days)) {
          const dates = data.days
            .map((day) => day.date)
            .filter((date) => date)
            .map((date) => formatDate(moment(date)))

          setOldItineraryDates(dates)
          setOldItineraryData(data.days)
          generateNewDateSequence(
            currentQuestStartDate,
            currentQuestEndDate,
            dates,
          )
        } else {
          setOldItineraryDates([])
          setOldItineraryData([])
          generateNewDateSequence(
            currentQuestStartDate,
            currentQuestEndDate,
            [],
          )
        }
      } catch (e) {
        if (e?.code !== 'validation_error') {
          Error.showError(e)
        }
        setOldItineraryDates([])
        setOldItineraryData([])
      } finally {
        setIsLoadingItinerary(false)
      }
    }

    fetchItineraryDates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedQuestId,
    sourceQuestId,
    show,
    currentQuestStartDate,
    currentQuestEndDate,
    generateNewDateSequence,
  ])

  const handleCreateClick = async () => {
    const days = itineraryMapping.map((mapping) => {
      if (mapping.oldDate === 'Keep Empty') {
        return {
          date: UTCformatDateInPlan(mapping.newDate),
          subTitle: '',
          imageFileName: '',
          itineraries: [],
        }
      }

      const originalDay = oldItineraryData.find(
        (day) => formatDate(moment(day.date)) === mapping.oldDate,
      )

      return {
        date: UTCformatDateInPlan(mapping.newDate),
        subTitle: originalDay?.subTitle || '',
        imageFileName: originalDay?.imageFileName || '',
        itineraries: (originalDay?.itineraries || []).map(
          ({ _id, ...itineraryItem }) => itineraryItem,
        ),
      }
    })

    if (onConfirmCopy) {
      await onConfirmCopy({ days })
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setOldItineraryDates([])
      setOldItineraryData([])
      setNewItineraryDates([])
      setItineraryMapping([])
      onHide()
    }
  }

  const handleOldDateChange = (index, value) => {
    const updatedMapping = [...itineraryMapping]
    updatedMapping[index].oldDate = value
    setItineraryMapping(updatedMapping)
  }

  const getAvailableDatesForDropdown = (currentIndex) => {
    const selectedDates = itineraryMapping
      .map((mapping, idx) => (idx !== currentIndex ? mapping.oldDate : null))
      .filter((date) => date && date !== 'Keep Empty')

    return oldItineraryDates.filter((date) => !selectedDates.includes(date))
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      handleClose()
    }
  }

  if (!show) return null

  const oldDateOptions = (index) => [
    { value: 'Keep Empty', label: 'Keep Empty' },
    ...getAvailableDatesForDropdown(index).map((date) => ({
      value: date,
      label: date,
    })),
  ]

  return (
    <div
      className='experience-copy-modal-backdrop open'
      role='dialog'
      aria-labelledby='copyItineraryModalTitle'
      aria-modal='true'
      onClick={handleBackdropClick}>
      <div className='experience-copy-modal-card experience-remap-card'>
        <div className='experience-copy-modal-scroll'>
          <h2 id='copyItineraryModalTitle'>{title}</h2>
          <p className='experience-copy-modal-lead'>{lead}</p>

          <div className='d-flex align-items-center mb-3'>
            <h4 className='experience-field-head-title mb-0'>
              Itinerary mapping
            </h4>
            {showOriginalLink && (sourceQuestId || selectedQuest?.value) ? (
              <Link
                to={`/admin/experiences/${
                  sourceQuestId || selectedQuest?.value
                }/itinerary`}
                target='_blank'
                rel='noopener noreferrer'
                className='experience-linkbtn ml-2'>
                (See original)
              </Link>
            ) : null}
          </div>

          {isLoadingItinerary ? (
            <div className='text-center py-3'>
              <ABSpinner size='sm' />
            </div>
          ) : !newItineraryDates.length ? (
            <div className='experience-copy-empty show'>
              No dates available for mapping. Please ensure this experience has
              start and end dates.
            </div>
          ) : (
            <>
              <div className='experience-map-head'>
                <span>Old dates</span>
                <span aria-hidden='true' />
                <span>New dates</span>
              </div>
              <div className='experience-map-rows'>
                {itineraryMapping.map((mapping, index) => (
                  <div key={mapping.id} className='experience-map-row'>
                    <ExperienceSelect
                      value={mapping.oldDate}
                      options={oldDateOptions(index)}
                      isSearchable={false}
                      disabled={isLoading}
                      onChange={(e) =>
                        handleOldDateChange(index, e.target.value)
                      }
                    />
                    <span className='experience-map-arrow' aria-hidden='true'>
                      →
                    </span>
                    <input
                      type='text'
                      className='experience-f-control'
                      value={mapping.newDate}
                      disabled
                      readOnly
                      tabIndex={-1}
                    />
                  </div>
                ))}
              </div>
            </>
          )}

          <div className='experience-copy-modal-actions'>
            <button
              type='button'
              className='btn-outline-experience'
              onClick={handleClose}
              disabled={isLoading}>
              Cancel
            </button>
            <button
              type='button'
              className='btn-navy'
              disabled={isLoading || !newItineraryDates.length}
              onClick={handleCreateClick}>
              {isLoading ? `${confirmLabel}…` : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

CopyItineraryModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectedQuest: PropTypes.object,
  sourceQuestId: PropTypes.string,
  currentQuestStartDate: PropTypes.string,
  currentQuestEndDate: PropTypes.string,
  onConfirmCopy: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  title: PropTypes.string,
  confirmLabel: PropTypes.string,
  showOriginalLink: PropTypes.bool,
  lead: PropTypes.string,
}

export default CopyItineraryModal
